import { useApiClient } from "@/src/hooks/useApiClient";
import { useQueryErrorToast } from "@/src/hooks/useQueryErrorToast";
import { useTranslations } from "@/src/i18n/i18n";
import { PAGE_SIZE } from "@/src/pages/Events/events.type";
import type { EventFilters } from "@/src/pages/Events/hooks/useEventFilters";

/**
 * La liste des événements, filtrée et paginée **par le serveur**.
 *
 * `GET /moderation/events` est la seule route qui énumère des événements pour
 * l'administration, et la seule qui rende les trois statuts — `close`
 * (« terminé ») compris, là où `get-nearby-events` les excluait en dur.
 *
 * ⚠️ **`lat`, `lng` et `radius` partent ensemble ou pas du tout.** Le serveur
 * refuse (400) un rayon sans centre, et il a raison : une requête acceptée mais
 * non appliquée rendrait une liste plus large que demandé, sans que rien ne le
 * signale. `useEventFilters` garantit déjà l'appariement, on ne fait ici que ne
 * pas le défaire.
 *
 * ⚠️ **`sort=distance` sans centre est refusé lui aussi**, et on ne l'envoie
 * donc jamais : une URL qui le porte quand même — copiée-collée après avoir
 * effacé le lieu — retombe sur le tri par date plutôt que de faire échouer la
 * requête. L'écran désactive l'onglet en parallèle, pour que le cas ne se
 * produise pas par le clic.
 *
 * `total` est un `count(*)` sur le filtre : il donne le nombre de pages, et ne
 * se compare pas à `events.length`.
 */
export function useEvents(filters: EventFilters) {
  const $api = useApiClient();
  const t = useTranslations("events");

  const hasCenter = filters.lat !== null && filters.lng !== null;
  const sort =
    filters.sort === "distance" && !hasCenter ? "date" : filters.sort;

  const { data, error, isError, isFetching } = $api.useQuery(
    "get",
    "/api/v1/moderation/events",
    {
      params: {
        query: {
          page: filters.page,
          limit: PAGE_SIZE,
          sort,
          order: filters.order,
          ...(filters.q.trim() ? { q: filters.q.trim() } : {}),
          // `all` n'est pas une valeur du serveur : `status` absent signifie
          // déjà « les trois ».
          ...(filters.status === "all" ? {} : { status: filters.status }),
          ...(hasCenter
            ? {
                lat: filters.lat ?? undefined,
                lng: filters.lng ?? undefined,
                radius: filters.radius,
              }
            : {}),
        },
      },
    },
  );

  useQueryErrorToast(isError, t("list-error"), error);

  const total = data?.data?.total ?? 0;

  return {
    events: data?.data?.events ?? [],
    total,
    pageCount: Math.max(1, Math.ceil(total / PAGE_SIZE)),
    // `isFetching` et non `isLoading` : en changeant de page, le cache de la
    // précédente rend `isLoading` faux et l'écran afficherait l'ancienne page
    // sans dire qu'il travaille.
    isLoading: isFetching,
    isError,
  };
}
